// @ts-nocheck
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'
import RouteInspector from './route-inspector'
import LogInspector from './log-inspector'
import HookInspector from './hook-inspector'
import ServiceInspector from './service-inspector'
import StoreInspector from './store-inspector'
import ExperimentInspector from './experiment-inspector'
import Icon from 'customer-flows/components/ui-kit/icon'
import { on } from '@ember/modifier'
import { assert } from '@ember/debug'
import { macroCondition, isDevelopingApp } from '@embroider/macros'
import type Owner from '@ember/owner'
import { fn, concat } from '@ember/helper'

export default class DebugToolbarComponent extends Component {
  @service flowManager: FlowManager
  @service session: Session

  @tracked selectedStep: FlowStep | null = null
  @tracked open: boolean = false
  @tracked activeTab: string = 'flow'
  @tracked toolbarWidth: number = 384 // 24rem default
  @tracked isResizing: boolean = false

  constructor(owner: Owner, args: object) {
    super(owner, args)
    this.open = this.session.restore()['debugToolbarOpen'] === 'true'
    this.activeTab = this.session.restore()['debugToolbarActiveTab'] ?? 'flow'

    const savedWidth = this.session.restore()['debugToolbarWidth']
    if (savedWidth) {
      this.toolbarWidth = parseInt(savedWidth, 10)
    }

    // This macro removes the assertion in dev
    // meaning that if the debug toolbar is imported in production it will throw an error
    if (macroCondition(!isDevelopingApp())) {
      assert('DebugToolbar should not be imported in production!')
    }

    this.updateBodyClass()
  }

  willDestroy() {
    super.willDestroy()
    document.body.classList.remove('flow-debug-toolbar-open')
    document.body.style.removeProperty('margin-left')
  }

  willDestroy() {
    super.willDestroy()
    document.body.classList.remove('flow-debug-toolbar-open')
    document.body.style.removeProperty('margin-left')
  }

  updateBodyClass() {
    if (this.open) {
      document.body.classList.add('flow-debug-toolbar-open')
      document.body.style.marginLeft = `${this.toolbarWidth}px`
    } else {
      document.body.classList.remove('flow-debug-toolbar-open')
      document.body.style.marginLeft = '0'
    }
  }

  @action
  startResize(event: MouseEvent) {
    event.preventDefault()
    this.isResizing = true

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizing) return

      const newWidth = Math.max(256, Math.min(800, e.clientX))
      this.toolbarWidth = newWidth
      this.updateBodyClass()
    }

    const onMouseUp = () => {
      this.isResizing = false
      this.session.persist({ debugToolbarWidth: String(this.toolbarWidth) })
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  get routes() {
    return this.flowManager.activeFlow?.pages ?? []
  }

  get activeRoute() {
    return this.flowManager.activeFlow?.activePage
  }

  get activeStep() {
    return this.flowManager.activeFlow?.activeStep
  }

  @action
  toggleOpen() {
    this.open = !this.open

    this.session.persist({ debugToolbarOpen: String(this.open) })
    this.updateBodyClass()
  }

  @action
  setActiveRoute(page: FlowPage) {
    this.flowManager.goToPage(page.type, { skipExitCheck: true })
  }

  @action
  selectStep(step: FlowStep) {
    if (this.selectedStep === step) {
      this.selectedStep = null
      this.session.persist({ debugToolbarSelectedStep: null })
      return
    }

    this.selectedStep = step
    this.session.persist({ debugToolbarSelectedStep: step?.id })
  }

  @action
  triggerHook(step: FlowStep, hook: string) {
    this.flowManager._triggerHook(step, hook)
    this.flowManager._emit(hook)
  }

  @action
  setActiveTab(tab: string) {
    this.activeTab = tab
    this.session.persist({ debugToolbarActiveTab: tab })
  }

  get isFlowTab() {
    return this.activeTab === 'flow'
  }

  get isServicesTab() {
    return this.activeTab === 'services'
  }

  get isStoresTab() {
    return this.activeTab === 'stores'
  }

  get isLogsTab() {
    return this.activeTab === 'logs'
  }

  get isExperimentsTab() {
    return this.activeTab === 'experiments'
  }

  get flowTabClass() {
    return this.activeTab === 'flow'
      ? 'border-theme-300 bg-neutrals-0 text-theme-300'
      : 'border-transparent text-neutrals-300 hover:bg-neutrals-100'
  }

  get servicesTabClass() {
    return this.activeTab === 'services'
      ? 'border-theme-300 bg-neutrals-0 text-theme-300'
      : 'border-transparent text-neutrals-300 hover:bg-neutrals-100'
  }

  get storesTabClass() {
    return this.activeTab === 'stores'
      ? 'border-theme-300 bg-neutrals-0 text-theme-300'
      : 'border-transparent text-neutrals-300 hover:bg-neutrals-100'
  }

  get logsTabClass() {
    return this.activeTab === 'logs'
      ? 'border-theme-300 bg-neutrals-0 text-theme-300'
      : 'border-transparent text-neutrals-300 hover:bg-neutrals-100'
  }

  get experimentsTabClass() {
    return this.activeTab === 'experiments'
      ? 'border-theme-300 bg-neutrals-0 text-theme-300'
      : 'border-transparent text-neutrals-300 hover:bg-neutrals-100'
  }

  <template>
    <div class='flow-debug-toolbar-container'>
      {{#if this.open}}
        <div
          data-testid='flow-kit__toolbar'
          class='flow-debug-toolbar shadow-cool-base border-neutrals-100 bg-neutrals-0 relative flex h-screen flex-col overflow-hidden border-r'
          style='width: {{this.toolbarWidth}}px;'
        >
          <div
            class='absolute right-0 top-0 z-50 h-full w-1 cursor-ew-resize transition-colors hover:bg-theme-300'
            {{on 'mousedown' this.startResize}}
          ></div>
          <div
            data-testid='flow-kit__toolbar__header'
            class='border-neutrals-100 bg-theme-50 flex w-full items-center justify-between border-b p-4'
          >
            <h1 class='text-xbold-header-20 text-neutrals-400'>Flow Toolbar</h1>
            <div class='flex gap-0.5'>
              {{! @glint-ignore: wait until we can't declare custom elements in glint }}
              <il-button variant='ghost' color='neutral' icon-only onClick={{this.toggleOpen}}>
                <Icon @src='close' />
              </il-button>
            </div>
          </div>

          <div class='border-neutrals-100 bg-neutrals-50 flex w-full border-b'>
            <button
              class='text-reg-para-14 flex-1 cursor-pointer border-b-2 px-4 py-2 transition-colors
                {{this.flowTabClass}}'
              {{on 'click' (fn this.setActiveTab 'flow')}}
            >Flow</button>
            <button
              class='text-reg-para-14 flex-1 cursor-pointer border-b-2 px-4 py-2 transition-colors
                {{this.servicesTabClass}}'
              {{on 'click' (fn this.setActiveTab 'services')}}
            >Services</button>
            <button
              class='text-reg-para-14 flex-1 cursor-pointer border-b-2 px-4 py-2 transition-colors
                {{this.storesTabClass}}'
              {{on 'click' (fn this.setActiveTab 'stores')}}
            >Stores</button>
            <button
              class='text-reg-para-14 flex-1 cursor-pointer border-b-2 px-4 py-2 transition-colors
                {{this.logsTabClass}}'
              {{on 'click' (fn this.setActiveTab 'logs')}}
            >Logs</button>
            <button
              class='text-reg-para-14 flex-1 cursor-pointer border-b-2 px-4 py-2 transition-colors
                {{this.experimentsTabClass}}'
              {{on 'click' (fn this.setActiveTab 'experiments')}}
            >A/B Tests</button>
          </div>

          <div data-testid='flow-kit__toolbar__body' class='flex w-full flex-1 flex-col overflow-auto'>
            {{#if this.isFlowTab}}
              <RouteInspector
                @routes={{this.routes}}
                @activeRoute={{this.activeRoute}}
                @activeStep={{this.activeStep}}
                @selectedStep={{this.selectedStep}}
                @onActivateRoute={{this.setActiveRoute}}
                @onSelectStep={{this.selectStep}}
              />

              {{#if this.selectedStep}}
                <div class='bg-neutrals-100 h-0.25 w-full' />
                <HookInspector @step={{this.selectedStep}} @triggerHook={{this.triggerHook}} />
              {{/if}}
            {{else if this.isServicesTab}}
              <ServiceInspector />
            {{else if this.isStoresTab}}
              <StoreInspector />
            {{else if this.isLogsTab}}
              <LogInspector />
            {{else if this.isExperimentsTab}}
              <ExperimentInspector />
            {{/if}}
          </div>
        </div>
      {{/if}}

      <button
        type='button'
        data-testid='flow-kit__toolbar__toggle'
        class='flow-debug-toolbar-toggle flex cursor-pointer items-center justify-center border border-neutrals-100 bg-neutrals-0 text-theme-300 transition-all hover:bg-theme-50'
        style='left: {{if this.open (concat this.toolbarWidth "px") "0"}};'
        data-tooltip={{if this.open 'Close Flow Toolbar' 'Open Flow Toolbar'}}
        {{on 'click' this.toggleOpen}}
      >
        <Icon @src={{if this.open 'carat-back' 'carat-forward'}} class='h-4 w-4' />
      </button>

      <style>
        :root {
          --log-debug-50: #FAFAFF;
          --log-debug-100: #E7E8F0;
          --log-debug-200: #CED1DF;
          --log-debug-300: #656A81;
          --log-debug-700: #414159;

          --log-info-50: #f4f6f0;
          --log-info-100: #dfeac9;
          --log-info-200: #a7b58a;
          --log-info-300: #6e7a4d;
          --log-info-700: #5b6642;

          --log-warn-50: #fefce8;
          --log-warn-100: #fef9c3;
          --log-warn-200: #fef08a;
          --log-warn-300: #fde047;
          --log-warn-700: #a16207;

          --log-error-50: #fef2f2;
          --log-error-100: #fee2e2;
          --log-error-200: #fecaca;
          --log-error-300: #fca5a5;
          --log-error-700: #b91c1c;
        }

        body {
          transition: margin-left 0.3s ease;
        }

        .flow-debug-toolbar-container {
          position: fixed;
          top: 0;
          left: 0;
          z-index: 9999;
          height: 100vh;
          pointer-events: none;
        }

        .flow-debug-toolbar {
          position: fixed;
          top: 0;
          left: 0;
          pointer-events: auto;
          box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
        }

        .flow-debug-toolbar-toggle {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: auto;
          transition: left 0.3s ease;
          height: 80px;
          width: 24px;
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          border-left: none;
          writing-mode: vertical-rl;
          padding: 8px 4px;
          box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
        }

        .flow-debug-toolbar-toggle::after {
          content: attr(data-tooltip);
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-left: 8px;
          padding: 6px 12px;
          background-color: white;
          color: #374151;
          font-size: 12px;
          white-space: nowrap;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease;
          z-index: 10000;
          writing-mode: horizontal-tb;
        }

        .flow-debug-toolbar-toggle:hover::after {
          opacity: 1;
        }


      </style>
    </div>
  </template>
}
